import { useGetInQueueSubscriptionsQuery } from "generated/graphql";
import { loader } from "graphql.macro";
import {
    convertHasuraSubscriptionsToSubscriptions,
    InQueueSubscription
} from "in_queue/types/projectType";
import { createContext, PropsWithChildren, useContext, useEffect } from "react";
import { Loading } from "types/loadingType";

interface InQueueSubscriptionsContextValue {
    subscriptions: Loading<InQueueSubscription[]>;
}

export const InQueueSubscriptionsContext =
    createContext<InQueueSubscriptionsContextValue>({
        subscriptions: "loading"
    });

const GET_IN_QUEUE_SUBSCRIPTION_UPDATES = loader(
    "src/graphql/in_queue/subscribeToInQueueSubscriptions.graphql"
);

export const InQueueSubscriptionsProvider: React.FC<
    PropsWithChildren<unknown>
> = ({ children }: PropsWithChildren<unknown>) => {
    let subscriptions: Loading<InQueueSubscription[]> = "loading";
    const { data: hasuraSubscriptions, subscribeToMore } =
        useGetInQueueSubscriptionsQuery();
    if (hasuraSubscriptions) {
        subscriptions =
            convertHasuraSubscriptionsToSubscriptions(hasuraSubscriptions);
    }

    useEffect(() => {
        if (subscriptions !== "loading") {
            // This makes sure that when in_queue_subscriptions changes, the frontend
            // receives the updates and re-renders accordingly
            const unsubscribe = subscribeToMore({
                document: GET_IN_QUEUE_SUBSCRIPTION_UPDATES,
                updateQuery: (prev, { subscriptionData }) => {
                    if (!subscriptionData.data) return prev;

                    return {
                        in_queue_subscriptions:
                            subscriptionData.data.in_queue_subscriptions
                    };
                }
            });

            return () => {
                unsubscribe();
            };
        }
    }, [subscribeToMore, subscriptions]);

    const value = {
        subscriptions
    };
    return (
        <InQueueSubscriptionsContext.Provider value={value}>
            {children}
        </InQueueSubscriptionsContext.Provider>
    );
};

export const useSubscriptions = (): Loading<InQueueSubscription[]> => {
    const context = useContext(InQueueSubscriptionsContext);
    return context.subscriptions;
};
